import { AlertTriangle, Check, Copy, Lock, ShieldCheck, UserRoundX } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaLineSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";
import { formatTimeAgo } from "@/shared/utils/dateTimeHelpers";

import type { OrganizationOverviewState } from "../hooks/useOrganizationOverview";
import {
  MEMBER_STATUS_COUNT_KEYS,
  MEMBER_STATUS_LABEL_KEYS,
  MEMBER_STATUS_PILL,
  memberStatuses,
} from "../utils/organizationOverview";

export interface OrganizationUsersPanelProps {
  state: OrganizationOverviewState;
  onRetry: () => void;
}

export function OrganizationUsersPanel({ state, onRetry }: OrganizationUsersPanelProps) {
  const { t, activeLanguage } = useTranslations("organizations");
  const { copiedValue, copy } = useCopyToClipboard();

  if (state.status === "forbidden") {
    return <ColocaAlert variant="warning" icon={Lock} text={t("overview_forbidden")} />;
  }

  if (state.status === "error") {
    return (
      <ColocaAlert
        variant="destructive"
        icon={AlertTriangle}
        text={t("overview_load_error")}
        inline
        actions={
          <ColocaButton variant="secondary" size="sm" onClick={onRetry}>
            {t("action_retry")}
          </ColocaButton>
        }
      />
    );
  }

  if (state.status !== "ready") {
    return (
      <ul className="divide-y divide-border rounded-lg border border-border">
        {[0, 1, 2].map((row) => (
          <li key={row} className="p-4">
            <ColocaLineSkeleton lines={2} />
          </li>
        ))}
      </ul>
    );
  }

  const { counts, items } = state.overview.users;

  if (counts.total === 0) {
    return (
      <ColocaEmpty
        icon={UserRoundX}
        title={t("users_empty_title")}
        description={t("users_empty_description")}
        surface="card"
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {memberStatuses(counts)
          .map(({ status, count }) => `${t(MEMBER_STATUS_COUNT_KEYS[status])} ${count}`)
          .join(" · ")}
      </p>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {items.map((user) => (
          <li key={user.id} className="flex items-start gap-3 p-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-medium text-foreground">{user.full_name}</p>
                {user.status !== "active" && (
                  <ColocaStatusPill variant={MEMBER_STATUS_PILL[user.status]} size="sm" withDot>
                    {t(MEMBER_STATUS_LABEL_KEYS[user.status])}
                  </ColocaStatusPill>
                )}
                {user.two_factor_enabled && (
                  <ShieldCheck
                    className="size-3.5 shrink-0 text-success"
                    aria-label={t("user_two_factor")}
                    role="img"
                  />
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-1">
                <span className="min-w-0 truncate text-xs text-muted-foreground" title={user.email}>
                  {user.email}
                </span>
                <ColocaIconButton
                  icon={copiedValue === user.email ? Check : Copy}
                  variant="ghost"
                  size="xs"
                  aria-label={t("copy_email_aria", { name: user.full_name })}
                  onClick={() => void copy(user.email, t("email_copied"))}
                />
              </div>
            </div>
            <p className="shrink-0 pt-0.5 text-right text-xs text-muted-foreground">
              {user.last_login_at
                ? t("user_last_login", {
                    when: formatTimeAgo(user.last_login_at, { locale: activeLanguage }),
                  })
                : t("user_never_logged_in")}
            </p>
          </li>
        ))}
      </ul>
      {counts.total > items.length && (
        <p className="text-xs text-muted-foreground">{t("users_truncated", { count: items.length })}</p>
      )}
    </div>
  );
}
